import OpenInNew from "@mui/icons-material/OpenInNew";
import { Box, IconButton, TextField, Tooltip } from "@mui/material";
import { CustomGridColDef, DataGrid } from "components/DataGrid";
import { TruncateDataGridCell } from "components/TruncateDataGridCell";
import { getStatusLabel } from "encamp-shared/src/submissions";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import { MouseEvent, useMemo } from "react";
import { Link } from "react-router-dom";

import { formatDay } from "./manifestCorrectionDisplay";
import { PICKER_GRID_DENSITY, PICKER_GRID_HEIGHT } from "./pickerGrid";

export type LinkableSubmissionOption = {
  id: string;
  title: string;
  submissionType: string;
  status: string;
  dueDate?: string | null;
  createdAt: string;
  facilityName?: string | null;
  assigneeName?: string | null;
  notes?: string | null;
};

type Props = {
  rows: LinkableSubmissionOption[];
  loading: boolean;
  search: string;
  onSearchChange: (value: string) => void;
  /** True when a search is narrowing the list — drives the empty-state copy. */
  searching: boolean;
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  /** Builds the per-row submission link-outs. */
  tenantId: string;
};

/**
 * Candidate-submissions grid for "mark resolved → link to an existing
 * submission". Presentational: the parent owns the query and the selection, so
 * a row filtered out by a search can't stay silently submittable.
 */
export function SubmissionPicker({
  rows,
  loading,
  search,
  onSearchChange,
  searching,
  selectedId,
  onSelect,
  tenantId,
}: Props) {
  const columns = useMemo<CustomGridColDef<LinkableSubmissionOption>[]>(
    () => [
      { field: "title", headerName: "Title", flex: 1, minWidth: 180 },
      {
        field: "notes",
        headerName: "Notes",
        flex: 1,
        minWidth: 160,
        sortable: false,
        renderCell: ({ row }) =>
          row.notes ? (
            <TruncateDataGridCell variant="body2" showTooltip preserveLineBreaks>
              {row.notes}
            </TruncateDataGridCell>
          ) : null,
      },
      {
        field: "submissionType",
        headerName: "Type",
        flex: 0.6,
        valueFormatter: (value) => (value != null ? prettyPrintEnumValue(String(value)) : ""),
      },
      {
        field: "status",
        headerName: "Status",
        flex: 0.6,
        valueFormatter: (value) => (value != null ? getStatusLabel(String(value)) : ""),
      },
      {
        field: "createdAt",
        headerName: "Created",
        flex: 0.5,
        valueFormatter: (value) => formatDay(value == null ? null : String(value)),
      },
      {
        field: "assigneeName",
        headerName: "Assignee",
        flex: 0.6,
        valueGetter: (_, row) => row.assigneeName ?? "Unassigned",
      },
      {
        field: "actions",
        headerName: "",
        width: 56,
        sortable: false,
        renderCell: (params) => (
          <Tooltip title="Open submission in a new tab">
            {/* One interactive element (a button that IS the link) — a button nested
                inside an anchor is invalid and double-stops keyboard focus. */}
            <IconButton
              size="small"
              component={Link}
              to={`/o/${tenantId}/waste/submissions/${params.row.id}/edit`}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open submission in a new tab"
              onClick={(e: MouseEvent) => e.stopPropagation()}
            >
              <OpenInNew fontSize="small" />
            </IconButton>
          </Tooltip>
        ),
      },
    ],
    [tenantId]
  );

  return (
    <Box sx={{ mt: 1 }}>
      <TextField
        label="Search submissions by title"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        fullWidth
        sx={{ mb: 1 }}
      />
      <Box sx={{ height: PICKER_GRID_HEIGHT }}>
        <DataGrid<LinkableSubmissionOption>
          rows={rows}
          columns={columns}
          loading={loading}
          noRowsMessage={
            searching
              ? "No submissions match that title."
              : "No recent submissions for this facility."
          }
          // Checkboxes make the single pick visible.
          checkboxSelection
          disableMultipleRowSelection
          rowSelectionModel={{
            type: "include",
            ids: new Set(selectedId ? [selectedId] : []),
          }}
          onRowSelectionModelChange={(model) => {
            const [id] = model.type === "include" ? Array.from(model.ids) : [];
            onSelect(id == null ? null : String(id));
          }}
          {...PICKER_GRID_DENSITY}
          sx={{
            // Single-select: hide select-all.
            "& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer": {
              display: "none",
            },
          }}
        />
      </Box>
    </Box>
  );
}
